const choices = [
  ['short', 'medium', 'long'],
  ['straight', 'wavy', 'curly'],
  ['natural', 'colored', 'processed'],
  ['balanced', 'dry', 'processed', 'length'],
  ['simple', 'complete']
];

export function recommendRoutine(answers) {
  if (answers.length !== choices.length || answers.some((answer, i) => !choices[i].includes(answer))) return null;
  const [length, , treatment, priority, scope] = answers;
  let series = 'refresh';
  if (priority === 'dry') series = 'hydration';
  else if (priority === 'processed') series = 'keratin';
  else if (priority === 'length') series = 'long';
  else if (treatment !== 'natural') series = 'keratin';
  else if (length === 'long') series = 'long';
  const handles = {
    hydration: ['oz-wet-shampoo', 'oz-wet-mask'],
    keratin: ['keratin-shampoo', 'keratin-mask'],
    long: ['long-hair-shampoo', 'long-hair-mask'],
    refresh: ['refresh-shampoo']
  }[series];
  if (scope === 'complete') {
    if (series === 'hydration') handles.push('four-factor-serum');
    else if (treatment !== 'natural' || series === 'keratin') handles.push('keratin-serum');
  }
  return { series, handles };
}

export function createQuizState() {
  return {
    step: -1,
    answers: [],
    start() { this.step = 0; this.answers = []; },
    answer(value) {
      if (!choices[this.step]?.includes(value)) return false;
      this.answers = [...this.answers.slice(0, this.step), value];
      this.step += 1;
      return true;
    },
    back() { this.step = Math.max(-1, this.step - 1); },
    reset() { this.step = -1; this.answers = []; }
  };
}

function initQuiz(root) {
  if (root.dataset.quizReady) return;
  // Liquid translations escape punctuation for HTML; decode once before using textContent.
  const copy = JSON.parse(root.querySelector('[data-quiz-copy]').textContent, (_key, value) => {
    if (typeof value !== 'string' || !/&(?:#\d+|#x[\da-f]+|[a-z]+);/i.test(value)) return value;
    return new DOMParser().parseFromString(value, 'text/html').body.textContent;
  });
  const state = createQuizState();
  const panel = root.querySelector('[data-quiz-panel]');
  const title = root.querySelector('[data-quiz-title]');
  const intro = root.querySelector('[data-quiz-intro]');
  const question = root.querySelector('[data-quiz-question]');
  const options = root.querySelector('[data-quiz-options]');
  const progress = root.querySelector('[data-quiz-progress]');
  const progressFill = root.querySelector('[data-quiz-progress-fill]');
  const counter = root.querySelector('[data-quiz-counter]');
  const back = root.querySelector('[data-quiz-back]');
  const result = root.querySelector('[data-quiz-result]');
  const products = root.querySelector('[data-quiz-products]');
  const productsList = root.querySelector('[data-quiz-products-list]');
  const photos = [...root.querySelectorAll('[data-quiz-photo]')];
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  let busy = false;
  let visualRequest = 0;

  async function showVisual(step) {
    const index = Math.max(0, Math.min(step, photos.length - 1));
    const photo = photos[index];
    const request = ++visualRequest;
    if (!photo) return;
    // Keep the previous portrait visible until the next image is ready.
    try { await photo.decode(); } catch { return; }
    if (request !== visualRequest) return;
    photos.forEach(item => item.classList.toggle('is-current', item === photo));
    root.dataset.quizVisual = String(index);
  }

  function animate(element, frames, duration) {
    if (reduced.matches || !element.animate) return Promise.resolve();
    return element.animate(frames, { duration, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }).finished.catch(() => {});
  }

  function render() {
    const isIntro = state.step < 0;
    const isResult = state.step === choices.length;
    showVisual(state.step);
    root.classList.toggle('quiz-active', !isIntro && !isResult);
    root.classList.toggle('quiz-show-result', isResult);
    intro.hidden = !isIntro;
    question.hidden = isIntro || isResult;
    progress.hidden = isIntro;
    back.hidden = isIntro;
    root.querySelector('[data-quiz-actions]').hidden = isIntro;
    result.hidden = !isResult;
    products.hidden = !isResult;
    root.querySelector('[data-quiz-restart]').hidden = !isResult;
    const count = Math.max(0, state.step);
    progress.setAttribute('aria-valuenow', String(count));
    progress.setAttribute('aria-valuetext', isResult ? copy.ready : `${copy.question} ${count + 1} / ${choices.length}`);
    progressFill.style.transform = `scaleX(${count / choices.length})`;
    counter.textContent = isResult ? copy.ready : `${copy.question} ${count + 1} / ${choices.length}`;
    if (isIntro) {
      title.textContent = copy.introTitle;
      productsList.replaceChildren();
      return;
    }
    if (isResult) {
      const recommendation = recommendRoutine(state.answers);
      const text = copy.results[recommendation.series];
      title.textContent = text.title;
      root.querySelector('[data-quiz-reason]').textContent = text.reason;
      root.querySelector('[data-quiz-scope-note]').textContent = recommendation.handles.length === 1 ? copy.oneStep : (recommendation.handles.length === 2 ? copy.twoSteps : copy.threeSteps);
      const profile = root.querySelector('[data-quiz-profile]');
      profile.replaceChildren();
      state.answers.forEach((answer, index) => {
        const item = document.createElement('li');
        item.textContent = copy.questions[index].options.find(option => option.value === answer).label;
        profile.append(item);
      });
      productsList.replaceChildren();
      recommendation.handles.forEach(handle => {
        const template = root.querySelector(`template[data-quiz-product="${handle}"]`);
        if (template) productsList.append(template.content.cloneNode(true));
      });
      root.querySelector('[data-quiz-no-products]').hidden = !!productsList.querySelector('.product-card');
      return;
    }
    const current = copy.questions[state.step];
    title.textContent = current.title;
    root.querySelector('[data-quiz-question-hint]').textContent = current.hint;
    options.replaceChildren();
    current.options.forEach(option => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'quiz-option';
      button.setAttribute('aria-pressed', String(state.answers[state.step] === option.value));
      const label = document.createElement('span');
      label.className = 'quiz-option-label';
      label.textContent = option.label;
      const detail = document.createElement('span');
      detail.className = 'quiz-option-detail';
      detail.textContent = option.detail;
      button.append(label, detail);
      button.addEventListener('click', () => {
        if (busy) return;
        options.querySelectorAll('button').forEach(item => item.setAttribute('aria-pressed', String(item === button)));
        transition(() => state.answer(option.value), button);
      });
      options.append(button);
    });
  }

  async function transition(update, selected) {
    if (busy) return;
    busy = true;
    panel.setAttribute('aria-busy', 'true');
    back.disabled = true;
    if (selected) await animate(selected, [{ opacity: .65 }, { opacity: 1 }], 180);
    await animate(panel, [{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0, transform: 'translateY(-8px)' }], 120);
    update();
    render();
    title.focus({ preventScroll: true });
    await animate(panel, [{ opacity: 0, transform: 'translateY(14px)', filter: 'blur(3px)' }, { opacity: 1, transform: 'translateY(0)', filter: 'blur(0)' }], 340);
    panel.removeAttribute('aria-busy');
    back.disabled = false;
    busy = false;
  }

  root.querySelector('[data-quiz-start]').addEventListener('click', () => transition(() => state.start()));
  back.addEventListener('click', () => transition(() => state.back()));
  root.querySelector('[data-quiz-restart]').addEventListener('click', () => transition(() => state.reset()));
  root.querySelector('[data-quiz-start]').hidden = false;
  root.dataset.quizReady = 'true';
  render();
}

if (typeof document !== 'undefined') {
  document.querySelectorAll('[data-hair-quiz]').forEach(initQuiz);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-hair-quiz]').forEach(initQuiz));
}
